/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import {
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  accessAPI,
  TablaFiltrable
} from "../../../shared";

/**
 * Tarjeta que muestra y administra los sitios adicionales de un proceso
 * (además del fabrica/fabricante). Permite agregar sitios del mismo cliente
 * que aún no estén vinculados, y desvincular los ya agregados.
 *
 * Props:
 *  - proceso: objeto con `id`, `cliente.id`, `sitiosAdicionales`,
 *    `sitioFabrica`, `sitioFabricante`.
 *  - onSitiosCambio({sitiosAdicionalesIds, sitiosAdicionales}): callback que
 *    el padre usa para mergear el cambio en su estado del proceso.
 */
export default function TarjetaSitiosAdicionales({ proceso, onSitiosCambio }) {
  const [sidebarAgregarVisible, setSidebarAgregarVisible] = useState(false);
  const [loaderAgregar, setLoaderAgregar] = useState(false);
  const [formKeyAgregar, setFormKeyAgregar] = useState(0);
  const [sitiosCliente, setSitiosCliente] = useState([]);
  const toast = useRef(null);

  // Sitios actuales del proceso (puede ser undefined antes del primer fetch).
  const sitiosAdicionales = proceso?.sitiosAdicionales || [];

  // Cargamos los sitios del cliente al montar (no al abrir el sidebar): el
  // Formulario congela su config en el primer render via `useState(config)`,
  // así que si el dropdown nace con `opciones: []` no se actualiza después.
  // Pre-cargar evita ese flash y el dropdown siempre abre con datos.
  useEffect(() => {
    if (!proceso?.cliente?.id) return;
    accessAPI(
      "GET",
      `admin/sitio/cliente/${proceso.cliente.id}`,
      null,
      (respuesta) => setSitiosCliente(respuesta),
      () => setSitiosCliente([])
    );
  }, [proceso?.cliente?.id]);

  function abrirSidebarAgregar() {
    setFormKeyAgregar((k) => k + 1);
    setSidebarAgregarVisible(true);
  }

  function agregarSitio(campos) {
    setLoaderAgregar(true);
    accessAPI(
      "POST",
      `admin/proceso/${proceso.id}/sitios-adicionales`,
      { sitioId: campos.sitioId },
      (respuesta) => {
        setLoaderAgregar(false);
        setSidebarAgregarVisible(false);
        if (onSitiosCambio) onSitiosCambio(respuesta);
        toast.current?.show({
          severity: "success",
          summary: "Sitio agregado",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderAgregar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar sitio",
          sticky: true
        });
      }
    );
  }

  function quitarSitio(sitio) {
    accessAPI(
      "DELETE",
      `admin/proceso/${proceso.id}/sitios-adicionales/${sitio.id}`,
      null,
      (respuesta) => {
        if (onSitiosCambio) onSitiosCambio(respuesta);
        toast.current?.show({
          severity: "success",
          summary: "Sitio quitado",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al quitar sitio",
          sticky: true
        });
      }
    );
  }

  // IDs ya vinculados al proceso (fabrica, fabricante y adicionales). Se usan
  // para excluirlos del dropdown de "agregar". Se calculan acá en vez de en
  // el render del config para que el callback `agregarSitio` y el config
  // queden simétricos.
  const idsExcluidos = new Set(
    [
      proceso?.sitioFabrica?.id,
      proceso?.sitioFabricante?.id,
      ...sitiosAdicionales.map((s) => s.id)
    ].filter(Boolean)
  );
  const opcionesDisponibles = sitiosCliente
    .filter((s) => !idsExcluidos.has(s.id))
    .map((s) => ({
      value: s.id,
      etiqueta: s.nombre || s.direccion
    }))
    .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es"));

  const formAgregarConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "sitioId",
            tipoitemformularioid: 5,
            etiqueta: "Sitio",
            requerido: true,
            sinOpcionInicial: true,
            opciones: opcionesDisponibles
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar sitio",
        validar: true,
        callback: agregarSitio
      }
    ]
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Card>
        <Titulo
          botones={
            <Boton icono negro callback={abrirSidebarAgregar}>
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Sitios adicionales
        </Titulo>

        <TablaFiltrable conFiltros={false}
          value={sitiosAdicionales}
          dataKey="id"
          stripedRows
          emptyMessage="No hay sitios adicionales vinculados"
          nombreArchivo="sitios_adicionales"
        >
          <Column
            header="Tipo"
            body={(s) => s.sitioTipo?.nombre || "-"}
          />
          <Column header="Nombre" body={(s) => s.nombre || "-"} />
          <Column
            body={(s) => (
              <Boton
                icono
                rojo
                chico
                confirmacion={`¿Quitar el sitio "${s.nombre || s.direccion}" del proceso?`}
                callback={() => quitarSitio(s)}
              >
                <i className="pi pi-trash" />
              </Boton>
            )}
            style={{ width: "3rem", textAlign: "center" }}
          />
        </TablaFiltrable>
      </Card>

      <Sidebar
        visible={sidebarAgregarVisible}
        onHide={() => setSidebarAgregarVisible(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Agregar sitio al proceso</Titulo>
        {opcionesDisponibles.length === 0 && sitiosCliente.length > 0 && (
          <p css={{ color: "var(--grisTextos)" }}>
            Todos los sitios del cliente ya están vinculados al proceso.
          </p>
        )}
        <Formulario
          key={formKeyAgregar}
          config={formAgregarConfig}
          loader={loaderAgregar}
        />
      </Sidebar>
    </>
  );
}
